import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { AnimationMode } from '../types/models';
import { findAnimationName } from '../utils/animations';
import {
  STEP_BY_STEP_ANIMATION_CANDIDATES,
  EXPLODED_VIEW_ANIMATION_CANDIDATES,
  STEP_BY_STEP_REQUIRED_KEYWORDS,
  EXPLODED_VIEW_REQUIRED_KEYWORDS,
} from '../constants/animations';

interface UseAnimationControllerOptions {
  animations: THREE.AnimationClip[];
  groupRef: React.RefObject<THREE.Group>;
  animationMode: AnimationMode;
  onDurationReady: (durationSeconds: number) => void;
}

interface UseAnimationControllerResult {
  /** Stable ref to the currently active AnimationAction */
  activeActionRef: React.MutableRefObject<THREE.AnimationAction | null>;
  /** Duration of the currently selected clip */
  activeClipDuration: number | null;
  /** Animation clip names from the loaded model */
  names: string[];
}

/**
 * Selects and drives the appropriate animation clip based on animationMode.
 *
 * Responsibilities:
 * - Connecting useAnimations to the model group
 * - Picking the best clip (step_by_step vs exploded_view vs fallback)
 * - Starting, stopping, play/pause sync
 * - Reporting animation duration via onDurationReady
 */
export function useAnimationController({
  animations,
  groupRef,
  animationMode,
  onDurationReady,
}: UseAnimationControllerOptions): UseAnimationControllerResult {
  const activeActionRef = useRef<THREE.AnimationAction | null>(null);
  const [actions, setActions] = useState<Record<string, THREE.AnimationAction> | null>(null);
  const [activeClipDuration, setActiveClipDuration] = useState<number | null>(null);
  const names = useMemo(
    () => animations.map((clip) => clip.name),
    [animations],
  );

  const stepByStepName = useMemo(
    () => findAnimationName(names, STEP_BY_STEP_ANIMATION_CANDIDATES, STEP_BY_STEP_REQUIRED_KEYWORDS),
    [names],
  );

  const explodedViewName = useMemo(
    () => findAnimationName(names, EXPLODED_VIEW_ANIMATION_CANDIDATES, EXPLODED_VIEW_REQUIRED_KEYWORDS),
    [names],
  );

  useEffect(() => {
    const root = groupRef.current;
    if (!root || animations.length === 0) {
      activeActionRef.current = null;
      setActions(null);
      setActiveClipDuration(null);
      return;
    }

    const mixer = new THREE.AnimationMixer(root);
    const nextActions = Object.fromEntries(
      animations.map((clip) => [clip.name, mixer.clipAction(clip, root)]),
    ) as Record<string, THREE.AnimationAction>;

    setActions(nextActions);

    return () => {
      Object.values(nextActions).forEach((action) => action.stop());
      mixer.stopAllAction();
      animations.forEach((clip) => mixer.uncacheClip(clip));
      mixer.uncacheRoot(root);
      activeActionRef.current = null;
      setActions(null);
      setActiveClipDuration(null);
    };
  }, [animations, groupRef]);

  // Select and start the correct animation clip
  useEffect(() => {
    if (!actions) {
      activeActionRef.current = null;
      setActiveClipDuration(null);
      return;
    }

    // Stop all running clips before switching
    Object.values(actions).forEach((a) => a?.stop());

    const selectedAnimationName =
      animationMode === 'exploded_view'
        ? (explodedViewName ?? stepByStepName)
        : (stepByStepName ?? explodedViewName);

    // Fall back to the first available clip if no named match was found
    const targetName = selectedAnimationName ?? names?.[0];
    if (!targetName || !actions[targetName]) {
      activeActionRef.current = null;
      setActiveClipDuration(null);
      return;
    }

    const selectedAction = actions[targetName];
    selectedAction.clampWhenFinished = true;
    selectedAction.loop = THREE.LoopOnce;
    selectedAction.reset().play();
    selectedAction.paused = false;

    activeActionRef.current = selectedAction;
    setActiveClipDuration(selectedAction.getClip().duration);
    onDurationReady(selectedAction.getClip().duration);
  }, [actions, animationMode, explodedViewName, names, onDurationReady, stepByStepName]);

  return { activeActionRef, activeClipDuration, names };
}
